import type {
  PartnerPackagingV2Set,
  PartnerPackagingV2SetCreate,
  PartnerPackagingV2SetUpdate,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { type LinkAssetPayload, type BulkLinkPayloads, linkMultipleAssets } from '../api-utils';

type SingleAssetLinkType = '';
type ArrayAssetLinkType = 'packagings' | 'tags';

export const packagingSets = {
  search: {
    queryKey: 'partner-management-service/packaging-sets',
    configurationType: 'default' as const,
    fn: async () => {
      const { data } = await GatewayAxiosClient.get<PartnerPackagingV2Set[]>(
        `/partner-management-proxy/api/v1/partners/packagingSets`,
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/packaging-sets/id',
    configurationType: 'no-cache' as const,
    fn: async (packagingSetId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerPackagingV2Set>(
        `/partner-management-proxy/api/v1/partners/packagingSets/${packagingSetId}`,
      );

      return data;
    },
  },
  create: async (packagingSet: PartnerPackagingV2SetCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerPackagingV2Set>(
      `/partner-management-proxy/api/v1/partners/packagingSets`,
      packagingSet,
    );

    return { data, invalidateQueryKeys: [['partner-management-service/packaging-sets']] };
  },
  update: async ({
    packagingSetId,
    packagingSet,
  }: {
    packagingSetId: string;
    packagingSet: PartnerPackagingV2SetUpdate;
  }) => {
    const { data } = await GatewayAxiosClient.put<PartnerPackagingV2Set>(
      `/partner-management-proxy/api/v1/partners/packagingSets/${packagingSetId}`,
      packagingSet,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/packaging-sets'],
        ['partner-management-service/packaging-sets/id', packagingSetId],
      ],
    };
  },
  link: async ({
    packagingSetId,
    payload,
  }: {
    packagingSetId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/packagingSets/${packagingSetId}/${payload.assetType}/${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/packaging-sets'],
        ['partner-management-service/packaging-sets/id', packagingSetId],
      ],
    };
  },
  bulkLink: async ({
    packagingSet,
    payloads,
  }: {
    packagingSet: PartnerPackagingV2Set;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerPackagingV2Set>(
      payloads,
      {
        packagings: packagingSet.packagingIds,
        tags: packagingSet.tagIds,
      },
      (linkPayload) => packagingSets.link({ packagingSetId: packagingSet.packagingSetId, payload: linkPayload }),
    );
  },
};
